// Skeletons: reproducen la forma de la pantalla mientras llegan los datos.
// Son marcas de posición, no pantallas en blanco; el brillo respeta "reducir movimiento".

export function Skeleton({ className = "", style }: { className?: string; style?: React.CSSProperties }) {
  return <span aria-hidden="true" className={`skeleton block rounded-pill ${className}`} style={style} />;
}

const CARD = "bg-surface-main-light dark:bg-surface-main-dark card-surface rounded-card p-5";

function TileSkeleton() {
  return (
    <div className={`${CARD} flex flex-col gap-3`}>
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-8 w-28" />
      <Skeleton className="h-10 w-full rounded-[10px]" />
    </div>
  );
}

function ChartCardSkeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`${CARD} flex flex-col gap-4 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="h-8 w-24" />
      <Skeleton className="h-[180px] w-full rounded-[12px]" />
    </div>
  );
}

// Skeleton del dashboard: misma rejilla que la pantalla real (tarjetas pequeñas y gráficos).
export function DashboardSkeleton() {
  return (
    <div role="status" aria-live="polite" aria-label="Cargando el panel" className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-80" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <TileSkeleton />
        <TileSkeleton />
        <TileSkeleton />
        <TileSkeleton />
      </div>
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-4">
        <ChartCardSkeleton className="xl:col-span-2" />
        <ChartCardSkeleton />
        <ChartCardSkeleton />
        <ChartCardSkeleton className="xl:col-span-2" />
        <ChartCardSkeleton className="xl:col-span-2" />
      </div>
    </div>
  );
}

// Skeleton de una pantalla de listado: título, barra de filtros y filas de tabla.
export function PageSkeleton() {
  return (
    <div role="status" aria-live="polite" aria-label="Cargando" className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Skeleton className="h-9 w-72 rounded-pill" />
        <Skeleton className="ml-auto h-10 w-64 rounded-pill" />
      </div>
      <div className={`${CARD} overflow-hidden p-0`}>
        <div className="flex flex-col">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-4 border-b border-surface-border-light/80 px-5 py-[18px] last:border-b-0 dark:border-surface-border-dark/80"
            >
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 flex-1" />
              <Skeleton className="hidden h-3 w-32 md:block" />
              <Skeleton className="h-6 w-20 rounded-pill" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Login mientras se verifica la sesión: la hoja de acceso con su forma final (título, Google, separador,
// opción de RUT). La hoja (vidrio, ancho, relleno) la define la página con `className`.
export function LoginSkeleton({ className = "" }: { className?: string }) {
  return (
    <div role="status" aria-live="polite" aria-label="Cargando" className={`flex flex-col ${className}`}>
      <div className="mb-8 flex flex-col items-center gap-3">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-64 max-w-full" />
      </div>
      <Skeleton className="h-[50px] w-full rounded-pill" />
      <Skeleton className="my-5 h-px w-full" />
      <Skeleton className="mx-auto h-11 w-56 rounded-pill" />
      <Skeleton className="mx-auto mt-6 h-4 w-72 max-w-full" />
    </div>
  );
}

// Marco de la aplicación mientras se verifica la sesión: barra lateral y contenido.
export function AppShellSkeleton() {
  return (
    <div role="status" aria-live="polite" aria-label="Cargando la intranet" className="flex h-dvh">
      <div className="glass border-0! hidden w-[280px] shrink-0 flex-col gap-4 rounded-none p-4 lg:flex">
        <div className="flex items-center gap-3 pt-2">
          <Skeleton className="h-9 w-9 rounded-[11px]" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-2.5 w-14" />
          </div>
        </div>
        <Skeleton className="h-10 w-full rounded-control" />
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded-control" />
        ))}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-6 overflow-hidden px-4 pt-6 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <TileSkeleton />
          <TileSkeleton />
          <TileSkeleton />
          <TileSkeleton />
        </div>
      </div>
    </div>
  );
}
